import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import {
    SuggestionDataMap,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { useCallback } from 'react'
import { extractSuggestions } from '../../ContentEditor/contentUtils'
import { getContentItemViewNodeElementMapping } from '../utils/TrackChangesPanelUtils'
import { useHandleCardPositions } from './useHandleCardPositions'
import { useProcessSubsequentSuggestions } from './useSubsequentProcessSuggestions'
import { useTrackChangeCards } from './useTrackChangeCards'

/**
 * Custom hook to process suggestion cards from content item view nodes.
 * It extracts suggestions and updates the suggestions store.
 *
 * @returns {Object} An object containing the processSuggestionCards function.
 */
export const useProcessSuggestionCards = () => {
    const { addOrUpdateSuggestions } = useSuggestionsShallowSelector((s) => ({
        addOrUpdateSuggestions: s.addOrUpdateSuggestions,
    }))
    const processSubsequentSuggestions = useProcessSubsequentSuggestions()
    const handleCardPositions = useHandleCardPositions()
    const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()
    const extractSuggestionsMap = useCallback(
        (elementMapping: Record<string, Partial<TextContentElement>>) => {
            // Use a plain object for less overhead than Map + fromEntries
            const suggestionsObj: Record<string, SuggestionDataMap> = {}

            for (const key in elementMapping) {
                const node = elementMapping[key]
                if (!node || (node.type === 'Text' && !node.text) || !node.id)
                    continue

                const suggestions = extractSuggestions([node])
                if (!suggestions?.length) continue

                suggestionsObj[node.id as string] = {
                    id: node.id as string,
                    suggestions,
                }
            }
            return suggestionsObj
        },
        []
    )

    const processSuggestionCards = useCallback(
        (
            contentItemViewNodes: Partial<ContentItemViewNodesType>[],
            shouldReturnSuggestions: boolean = false
        ) => {
            try {
                if (!contentItemViewNodes.length)
                    return shouldReturnSuggestions ? {} : undefined

                const elementMapping = getContentItemViewNodeElementMapping(
                    contentItemViewNodes
                )
                const suggestionsMap = processSubsequentSuggestions(
                    extractSuggestionsMap(elementMapping),
                    elementMapping
                )

                if (shouldReturnSuggestions) {
                    return suggestionsMap
                }
                if (!isNotDefaultSuggestionCardFilter) {
                    handleCardPositions(suggestionsMap)
                }
                addOrUpdateSuggestions(suggestionsMap, 'replace')
            } catch (error) {
                console.error('Error in processSuggestionCards:', error)
                return shouldReturnSuggestions ? {} : undefined
            }
        },
        [
            addOrUpdateSuggestions,
            processSubsequentSuggestions,
            handleCardPositions,
            isNotDefaultSuggestionCardFilter,
        ]
    )

    return { processSuggestionCards }
}
